/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import debounce from 'lodash.debounce';
import dynamic from 'next/dynamic';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {sortByString} from '@ion/core/src/utils/ArrayUtil';
import {safeJSONStringify} from '@ion/core/src/utils/JSONUtils';
import {getSelectedFilters} from '@ion/dls/src/filters/SelectedFilters';
import {
  ALL_FEATURES_DEF,
  FLEET_ASSET_LAYER_ID,
  FLEET_SELECTED_ASSET_LAYER_ID,
  LIVE_HISTORY_LAST_POSITION_LAYER_ID,
  LIVE_HISTORY_LAYER_ID,
  LIVE_HISTORY_OPTION_LIST
} from '@ion/dls/src/maps/constants';
import {getLatestRecordByField, getLayerByTitle} from '@ion/dls/src/maps/helpers/MapViewUtils';
import {MapContainerProps} from '@ion/dls/src/maps/types/types';
import {useThemeContext} from '@ion/dls/src/theme/useThemeContext';
import {ToolbarEnum, TrackingMode} from '@ion/dls/src/types/types';
import {
  cancelLiveHistory,
  clearHistorySearchAssets,
  clearLiveHistoryAssetSearchList,
  runLiveHistory,
  setLiveHistoryAssetList,
  setLiveHistoryAssetSearchActive,
  setLiveHistoryError,
  setLiveHistoryFetching,
  setLiveHistoryHideNonSelectedAssets,
  setLiveHistoryInitialAssets,
  setLiveHistoryInputEvents,
  setLiveHistoryIOProfileFetching,
  setLiveHistoryReportType,
  setLiveHistorySearchTime,
  setLiveHistorySelectedAsset,
  setMapWidgetSelected,
  setPersistLiveHistory,
  setTrackingMapLayers
} from '@/store/actions/tracking';
import {
  globalGroupIdSelector
} from '@/store/selectors/global-group-chooser-selectors';
import {
  gridFilterModelSelector,
  isSelectedFiltersActiveSelector,
  liveHistoryAssetSearchActiveSelector,
  liveHistoryAssetSearchListSelector,
  liveHistoryHideNonSelectedAssetsActive,
  liveHistoryInitialAssetsSelector,
  liveHistoryInputEventsSelector,
  liveHistoryIsFetchingSelector,
  liveHistoryIsIOProfileFetchingSelector,
  liveHistoryIsUIFetchingSelector,
  liveHistoryPersistActiveSelector,
  liveHistoryReportTypeSelector,
  liveHistoryRingsSelector,
  liveHistorySearchTimeSelector,
  mapLayerSelector,
  mapWidgetSelectedSelector,
  trackingFiltersSelector,
  trackingGridStateSelector,
  trackingModeSelector
} from '@/store/selectors/tracking-selectors';
import {
  ASSET_MAPPING,
  CATEGORY_DEFINITION,
  CATEGORY_MAPPING,
  createQueryDefinition,
  excludeItemsByField,
  getNotFullySelectedFilters,
  mapAndDeduplicate,
  ROUTE_DEFINITION,
  ROUTE_MAPPING
} from '../../../helpers';
import useMapToolProps from '../hooks/useMapToolProps';

const ASSET_SEARCH_THRESHOLD = 120;

const DynamicLiveHistoryContainer = dynamic(() => import('@ion/dls/src/maps/liveHistory/LiveHistoryContainer'), {
  ssr : false
});

/**
* @LiveHistoryContainer
*
* Defines a container to wrap the Live History implementation
*
* ```typescript
*
*   import LiveHistoryContainer from '@ion/dls/src/maps/liveHistory/LiveHistoryContainer';
*
*   <LiveHistoryContainer map={map} view={view} />
*
* ```
*/
const LiveHistoryContainer = ({map, view}: MapContainerProps) => {
  const dispatch                        = useDispatch();
  const isMounted                       = useHasMounted(null);
  const mapToolProps                    = useMapToolProps();
  const fleetAssetLayerViewRef          = useRef(null);
  const fleetAssetLayerRef              = useRef(null);
  const fleetSelectedAssetLayerRef      = useRef(null);
  const liveHistoryLastPositionLayerRef = useRef(null);
  const liveHistoryPersistActive        = useSelector(liveHistoryPersistActiveSelector);
  const trackingMode                    = useSelector(trackingModeSelector);
  const layers                          = useSelector(mapLayerSelector);
  const mapWidgetSelected               = useSelector(mapWidgetSelectedSelector);
  const reportType                      = useSelector(liveHistoryReportTypeSelector);
  const assetsSearchActive              = useSelector(liveHistoryAssetSearchActiveSelector);
  const inputEventList                  = useSelector(liveHistoryInputEventsSelector);
  const searchIntervalTime              = useSelector(liveHistorySearchTimeSelector);
  const assetsSearchList                = useSelector(liveHistoryAssetSearchListSelector);
  const isHideNonSelectedAssetsActive   = useSelector(liveHistoryHideNonSelectedAssetsActive);
  const initialAssets                   = useSelector(liveHistoryInitialAssetsSelector);
  const globalGroupId                   = useSelector(globalGroupIdSelector);
  const filters                         = useSelector(trackingFiltersSelector);
  const {filterApplied}                 = useSelector(trackingGridStateSelector);
  const gridFilterModel                 = useSelector(gridFilterModelSelector);
  const isFetching                      = useSelector(liveHistoryIsFetchingSelector);
  const isUIFetching                    = useSelector(liveHistoryIsUIFetchingSelector);
  const isIOProfileFetching             = useSelector(liveHistoryIsIOProfileFetchingSelector);
  const hasTrackingFilterActive         = useSelector(isSelectedFiltersActiveSelector);
  const layersRef                       = useRef(layers);
  const assetsSearchListRef             = useRef(assetsSearchList);
  const {onLiveHistoryUIFetching}       = mapToolProps || {};
  const {isCurrent}                     = useThemeContext();
  const selectedFilters                 = getSelectedFilters(filters);
  const historyRings                    = useSelector(liveHistoryRingsSelector);
  const historyRingsRef                 = useRef(historyRings);

  const [assetsSearchOptions, setAssetsSearchOptions]   = useState([]);
  const [resetLocalAssetState, setResetLocalAssetState] = useState(false);

  // Assets, Category and Route local state
  const [categories, setCategories] = useState<LIVE_HISTORY_OPTION_LIST[] | unknown[]>([]);
  const [routes, setRoutes]         = useState<LIVE_HISTORY_OPTION_LIST[] | unknown[]>([]);
  const [assets, setAssets]         = useState<LIVE_HISTORY_OPTION_LIST[] | unknown[]>([]);

  const isTrackingLive              = trackingMode === TrackingMode.Live;

  const getAssetsCategoriesAndRoutes = async() => {
    getLayerFromMap(map, view);
    const [assets, categories, routes] = await Promise.all([
      getAssetsFromFleetLayer(ALL_FEATURES_DEF),
      getAssetsFromFleetLayer(CATEGORY_DEFINITION),
      getAssetsFromFleetLayer(ROUTE_DEFINITION)
    ]);

    setAssets(mapAndDeduplicate(
      assets,
      ASSET_MAPPING,
      'assetId'
    ));
    setCategories(mapAndDeduplicate(
      categories,
      CATEGORY_MAPPING,
      'assetCategory'
    ));
    setRoutes(mapAndDeduplicate(
      routes,
      ROUTE_MAPPING,
      'routeId'
    ));
  };

  useEffect(() => {
    if (mapWidgetSelected === ToolbarEnum.LiveHistory) {
      getAssetsCategoriesAndRoutes();
    }
  }, [mapWidgetSelected]);

  // a filter/group/grid change invalidates whatever the Live History widget
  // had already searched over, so its own run is cancelled - but only when
  // that widget is the one actually running, or this cancels a Timeline
  // Mode report the same change has nothing to do with ...
  useEffect(() => {
    if (mapWidgetSelected === ToolbarEnum.LiveHistory) {
      setResetLocalAssetState(!resetLocalAssetState);
      onCancel();
    }
  }, [globalGroupId, gridFilterModel, filterApplied, safeJSONStringify(selectedFilters)]);

  // switching themes hands whatever the widget last displayed over to the
  // other theme - the current theme's Timeline takes the run over and puts
  // the widget away without its own Close, which is what would otherwise
  // forget the assets it last displayed. The panel stays mounted while
  // hidden, so left remembering them, a return to the legacy theme finds
  // Update Display disabled over the very same asset and interval ...
  useEffect(() => {
    setResetLocalAssetState(isReset => !isReset);
  }, [isCurrent]);

  useEffect(() => {
    assetsSearchListRef.current = assetsSearchList;
  }, [assetsSearchList]);

  useEffect(() => {
    layersRef.current = layers;
  }, [layers]);

  useEffect(() => {
    historyRingsRef.current = historyRings;
  }, [historyRings]);

  useEffect(() => {
    getLayerFromMap(map, view);
    return () => {
      onCancel?.();
    };
  }, []);

  const getAssetsFromFleetLayer = useCallback(async(definition: string) => {
    const {filter}        = fleetAssetLayerViewRef.current || {};
    const queryDefinition = createQueryDefinition(filter?.where, definition);
    const results         = await getAssetsFromFleetLayers(queryDefinition);
    return results;
  }, []);

  const getQuery = useCallback((layer: __esri.FeatureLayer, queryDefinition: string) => {
    const query = layer.createQuery();
    query.where = queryDefinition;
    return query;
  }, []);

  const getAssetsFromFleetLayers = useCallback(async(queryDefinition) => {
    if (fleetAssetLayerRef.current && fleetSelectedAssetLayerRef.current) {
      const query       = getQuery(fleetAssetLayerRef.current, queryDefinition);
      const fleetAssets = await fleetAssetLayerRef.current.queryFeatures(query)
        .then(({features}) => features);
      const querySelected  = getQuery(fleetSelectedAssetLayerRef.current, queryDefinition);
      const selectedAssets = await fleetSelectedAssetLayerRef.current.queryFeatures(querySelected)
        .then(({features}) => features);
      const results      = [...fleetAssets, ...selectedAssets];
      return results;
    }
    return [];
  }, []);

  /**
   * Fetches and selects the asset data based on the given asset ID.
   * It first attempts to retrieve the asset from the live history last position layer.
   * If not found, it falls back to querying the fleet layers.
   * The selected asset is then stored in Redux state (or cleared if not found).
   */
  const setSelectedAsset = async(selectedAssetId) => {
    if (!selectedAssetId) {
      dispatch(setLiveHistorySelectedAsset(null));
      return;
    }

    let results = [];
    // Ensure the last position layer is loaded
    if (!liveHistoryLastPositionLayerRef.current) {
      liveHistoryLastPositionLayerRef.current = getLayerByTitle(
        map?.layers as any,
        LIVE_HISTORY_LAST_POSITION_LAYER_ID
      );
    }

    const query = `assetId = ${selectedAssetId}`;
    if (liveHistoryLastPositionLayerRef.current) {
      const lastPositionQuery = getQuery(liveHistoryLastPositionLayerRef.current, query);
      results = await liveHistoryLastPositionLayerRef.current?.queryFeatures(lastPositionQuery)
        .then(({features}) => features);
    }
    
    // Fallback to fleet layers if no results
    if (!results?.length) {
      results = await getAssetsFromFleetLayers(query);
    } else {
      const assetAttributes = results.map(f => f.attributes);
      const latest = getLatestRecordByField(assetAttributes, selectedAssetId, 'eventTime') as any;
      results = [{attributes: latest}];
    }
    const {attributes} = results[0] || {};
    if (attributes) {
      dispatch(setLiveHistorySelectedAsset({
        ...attributes,
        isCvsAsset : true
      }));
    } else {
      dispatch(setLiveHistorySelectedAsset(null));
    }
  };

  const assetExists = useCallback(value => {
    return assetsSearchListRef.current?.some(({assetId}) => assetId === value);
  }, []);

  const clearAssetsSearchList = useCallback(selectedIds => {
    dispatch(clearLiveHistoryAssetSearchList(selectedIds));
  }, []);

  const onToggleHideNonSelectedAssets = useCallback(status => {
    dispatch(setLiveHistoryHideNonSelectedAssets(status));
  }, []);

  const onDisplay = useCallback(async() => {
    if (assetsSearchListRef.current?.length) {
      const query   = `assetId IN (${assetsSearchListRef.current.map(({assetId}) => assetId)})`;
      const results = await getAssetsFromFleetLayers(query);
      if (results.length) {
        const attributes = results.map(({attributes}) => attributes);
        dispatch(setLiveHistoryInitialAssets(attributes));
      } else {
        dispatch(setLiveHistoryInitialAssets(results));
      }
    } else {
      dispatch(setLiveHistoryInitialAssets([]));
    }
    dispatch(setTrackingMapLayers([...layers, LIVE_HISTORY_LAYER_ID]));
    dispatch(runLiveHistory());
  }, [layers]);

  const onCancel = useCallback(() => {
    if (historyRingsRef.current?.length) {
      dispatch(clearHistorySearchAssets());
    } else {
      const currentLayers = layersRef.current
        ?.filter(layerId => layerId !== LIVE_HISTORY_LAYER_ID);
      setInputEvents([]);
      if (mapWidgetSelected === ToolbarEnum.LiveHistory && liveHistoryPersistActive) {
        currentLayers.push('current');
      }
      dispatch(setTrackingMapLayers(currentLayers));
      dispatch(cancelLiveHistory());
    }
    dispatch(setPersistLiveHistory(false));
    dispatch(setMapWidgetSelected(null));
    dispatch(setLiveHistoryFetching(false));
  }, [liveHistoryPersistActive, mapWidgetSelected]);
  
  /**
   * Handles selection of assets from the autocomplete list.
   * 
   * - Skips processing if no values are provided.
   * - Iterates through selected values and checks if the asset already exists.
   * - If not, increments a counter and dispatches the asset to the store.
   * - Stops and sets an error if the number of selected assets exceeds the threshold.
   * - Clears the autocomplete options after processing.
   */
  const onOptionSelect = useCallback((event, values) => {
    if (!values) return;

    dispatch(setLiveHistoryIOProfileFetching(true));
    const valuesToInsert = [];
    let count = assetsSearchListRef.current?.length || 0;

    for (let i = 0; i < values.length; i++) {
      const value = values[i];
      if (value && !assetExists(value?.assetId)) {
        count++;
        if (count > ASSET_SEARCH_THRESHOLD) {
          dispatch(setLiveHistoryError(true, count - 1));
          break;
        }
        valuesToInsert.push(value);
      }
    }
    dispatch(setLiveHistoryAssetList(valuesToInsert) as any);
    setAssetsSearchOptions([]);
  }, []);

  const onSearchTimeChange = useCallback(time => {
    dispatch(setLiveHistorySearchTime(time));
  }, []);
  
  const setInputEvents = useCallback(inputEvents => {
    dispatch(setLiveHistoryInputEvents(inputEvents));
  }, []);

  const getLayerFromMap = useCallback((map: __esri.Map, view: __esri.MapView) => {
    if (!fleetAssetLayerRef.current || !fleetAssetLayerViewRef.current || !liveHistoryLastPositionLayerRef.current) {
      const mapLayers                         = map?.layers;
      fleetAssetLayerRef.current              = getLayerByTitle(mapLayers as any, FLEET_ASSET_LAYER_ID);
      fleetSelectedAssetLayerRef.current      = getLayerByTitle(mapLayers as any, FLEET_SELECTED_ASSET_LAYER_ID);
      liveHistoryLastPositionLayerRef.current = getLayerByTitle(mapLayers as any, LIVE_HISTORY_LAST_POSITION_LAYER_ID);
      fleetAssetLayerRef.current?.when(async() => {
        fleetAssetLayerViewRef.current = await view.whenLayerView(fleetAssetLayerRef.current);
      });
    }
  }, []);

  const onSearchChange = useCallback(debounce(({target: {value}}) => {
    if (!fleetAssetLayerRef.current || !fleetAssetLayerViewRef.current || !liveHistoryLastPositionLayerRef.current) {
      getLayerFromMap(map, view);
      fleetAssetLayerRef.current?.when(async() => {
        fleetAssetLayerViewRef.current = await view.whenLayerView(fleetAssetLayerRef.current);
        queryFleetAssetLayer(value);
      });
    } else {
      queryFleetAssetLayer(value);
    }
  }, 350), [map, view]);

  const queryFleetAssetLayer = useCallback(async(value) => {
    if (value?.trim()?.length) {
      const assets    = [];
      const {filter}  = fleetAssetLayerViewRef.current || {};
      const query     = fleetAssetLayerRef.current.createQuery();
      query.outFields = ['assetId', 'assetName', 'assetType'];
      if (filter?.where) {
        query.where = filter?.where;
      }
      const assetDefinition = `Upper(assetName) like '%${value?.toUpperCase()}%'`;
      query.where          += (filter?.where || query.where) ? ` AND ${assetDefinition}` : assetDefinition;
      query.orderByFields  = ['assetName'];
      const fleetAssets    = await fleetAssetLayerRef.current.queryFeatures(query)
        .then(({features}) => features);
      const selectedAssets = await fleetSelectedAssetLayerRef.current.queryFeatures(assetDefinition)
        .then(({features}) => features);
      const selectedHistoryAssets = await liveHistoryLastPositionLayerRef.current?.queryFeatures(assetDefinition)
        .then(({features}) => features) || [];
      const results        = [...fleetAssets, ...selectedAssets, ...selectedHistoryAssets];
      if (results?.length) {
        results.forEach(({attributes}) => {
          const {assetId, assetName, assetType} = attributes;
          const selectedAssets = [...assetsSearchListRef.current || [], ...assets];
          if (!selectedAssets?.some(asset => asset.assetId === assetId)) {
            assets.push({
              assetId,
              assetType,
              id    : assetId,
              title : assetName
            });
          }
        });
        const sortedAssets = sortByString(assets, 'title');
        setAssetsSearchOptions(sortedAssets);
      } else {
        setAssetsSearchOptions([]);
      }
    } else {
      setAssetsSearchOptions([]);
    }
  }, []);

  const onSearchInputChange = useCallback(function(event, value, reason) {
    if (reason === 'clear') {
      setAssetsSearchOptions([]);
    }
  }, []);

  const setHistoryReportType = useCallback(reportType => {
    dispatch(setLiveHistoryReportType(reportType));
  }, []);

  const setHistoryAssetSearchActive = useCallback(active => {
    dispatch(setLiveHistoryAssetSearchActive(active));
  }, []);

  const assetList = useMemo(() =>
    excludeItemsByField(assets, assetsSearchList, 'assetId')
  , [assets, assetsSearchList]);

  const categoryList = useMemo(() =>
    getNotFullySelectedFilters({
      data         : assets,
      selectedData : assetsSearchList,
      filterData   : categories,
      field        : 'assetCategory'
    })
  , [assets, assetsSearchList, categories]);

  const routeList = useMemo(() =>
    getNotFullySelectedFilters({
      data         : assets,
      selectedData : assetsSearchList,
      filterData   : routes,
      field        : 'routeId'
    })
  , [assets, assetsSearchList, routes]);
  
  const liveHistoryProps = useMemo(() => ({
    cancel                   : onCancel,
    display                  : onDisplay,
    selectedTool             : mapWidgetSelected,
    assetsOptions            : assetsSearchOptions,
    setLiveHistoryUIFetching : onLiveHistoryUIFetching,
    setAssetSearchActive     : setHistoryAssetSearchActive,
    assets                   : assetList,
    routes                   : routeList,
    categories               : categoryList,
    isFetching,
    reportType,
    isUIFetching,
    initialAssets,
    setInputEvents,
    inputEventList,
    onOptionSelect,
    assetsSearchList,
    setSelectedAsset,
    searchIntervalTime,
    assetsSearchActive,
    onSearchTimeChange,
    isIOProfileFetching,
    resetLocalAssetState,
    setHistoryReportType,
    clearAssetsSearchList,
    hasTrackingFilterActive,
    isHideNonSelectedAssetsActive,
    onToggleHideNonSelectedAssets
  }), [
    onCancel,
    onDisplay,
    assetList,
    routeList,
    isFetching,
    reportType,
    categoryList,
    isUIFetching,
    initialAssets,
    setInputEvents,
    inputEventList,
    onOptionSelect,
    assetsSearchList,
    setSelectedAsset,
    mapWidgetSelected,
    searchIntervalTime,
    assetsSearchActive,
    onSearchTimeChange,
    isIOProfileFetching,
    assetsSearchOptions,
    resetLocalAssetState,
    setHistoryReportType,
    clearAssetsSearchList,
    setLiveHistoryFetching,
    hasTrackingFilterActive,
    setHistoryAssetSearchActive,
    isHideNonSelectedAssetsActive,
    onToggleHideNonSelectedAssets
  ]);
  
  if (isMounted && isTrackingLive) {
    return (
      <DynamicLiveHistoryContainer map={map}
        view={view}
        {...liveHistoryProps} />
    );
  }
};

export default LiveHistoryContainer;
